import { buildPermission } from "common/utils/auth";
import { Button, capitalize, Paper, Typography } from "@mui/material";
import { carbonConvert } from "portal/utils/metrics";
import { CarbonMeasurementSystem, CarbonUnit } from "common/utils/units/units";
import { changeArchetype, TractorViewModel } from "../TractorViewModel";
import { classes } from "common/theme/theme";
import { EditEquipmentButton } from "portal/views/equipment/EditEquipmentButton";
import { hitchTypeToLocaleString } from "common/utils/enumTranslations";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { roundToNPlaces } from "common/utils/numbers";
import { TractorArchetype } from "protos/atk/autotractor";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import React, { FC } from "react";

interface TractorArchetypeChooserProps {
  archetype: TractorArchetype;
  disabled?: boolean;
}
export const TractorArchetypeChooser: FC<TractorArchetypeChooserProps> = ({
  archetype,
  disabled,
}) => {
  const { t } = useTranslation();
  const localSystem = useSelf().measurementSystem;
  const formikProps = useFormikContext<TractorViewModel>();
  const wheelbaseUnits: {
    [system in CarbonMeasurementSystem]: CarbonUnit;
  } = { imperial: "ft", metric: "m" };
  const wheelbaseUnit = wheelbaseUnits[localSystem];
  const wheelBaseValue = roundToNPlaces(
    carbonConvert(archetype.wheelbaseM, "m", wheelbaseUnit),
    2
  );
  const wheelSpacingUnits: {
    [system in CarbonMeasurementSystem]: CarbonUnit;
  } = { imperial: "ft", metric: "m" };
  const wheelSpacingUnit = wheelSpacingUnits[localSystem];
  const wheelSpacingValue = roundToNPlaces(
    carbonConvert(archetype.wheelSpacingM, "m", wheelbaseUnit),
    2
  );
  const isSelected = formikProps.values.archetype?.id === archetype.id;
  const canEditArchetypes = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );
  return (
    <Paper
      className={classes(
        "w-96 flex flex-col gap-4 p-4 rounded-xl h-72",
        isSelected && "border-4 border-carbon-orange border-solid" // would love to use primary, but that's blue lol
      )}
      variant="outlined"
    >
      <div className="flex flex-col">
        <div className="flex gap-2">
          <Typography variant="h6">{archetype.make}</Typography>
          <Typography variant="h6">{archetype.model}</Typography>
          <div className="flex gap-1 ml-auto">
            {isSelected && <CheckIcon className="flex" color="primary" />}
            {canEditArchetypes && (
              <EditEquipmentButton
                type="tractor-archetype"
                variant="icon"
                equipmentId={archetype.id}
              />
            )}
          </div>
        </div>
        <Typography color={"InactiveCaptionText"}>
          {archetype.series}
        </Typography>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between gap-2">
          <Typography color={"InactiveCaptionText"}>
            {capitalize(t("models.equipment.wheelSpacing"))}
          </Typography>
          <Typography>{`${wheelSpacingValue} ${wheelSpacingUnit}`}</Typography>
        </div>
        <div className="flex justify-between gap-2">
          <Typography color={"InactiveCaptionText"}>
            {capitalize(t("models.equipment.wheelBase"))}
          </Typography>
          <Typography>{`${wheelBaseValue} ${wheelbaseUnit}`}</Typography>
        </div>
        <div className="flex justify-between gap-2">
          <Typography color={"InactiveCaptionText"}>
            {capitalize(t("models.equipment.hitch_other"))}
          </Typography>
          <Typography>
            {archetype.hitches?.hitches
              .map((h) => t(hitchTypeToLocaleString(h.type)))
              .join(", ")}
          </Typography>
        </div>
      </div>
      <Button
        className="mt-auto"
        variant="contained"
        onClick={() => changeArchetype(formikProps, archetype)}
        disabled={isSelected || disabled}
      >
        {t("utils.actions.selectShort")}
      </Button>
    </Paper>
  );
};
